import { normalizeGivingPlatformCategory } from "../features/giving/resolveDonationIntent";

const DEFAULT_AMOUNTS = [25, 50, 100, 250];

const DEFAULT_CATEGORY_CONFIG = {
  campaign: {
    key: "campaign",
    label: "Campaign",
    description: "Support active initiatives and impact projects.",
    suggestedAmounts: [25, 50, 100, 250],
    allowMonthly: true,
    allowOneTime: true,
  },
  tithe: {
    key: "tithe",
    label: "Tithes",
    description: "A faithful gift supporting the ministry and mission.",
    suggestedAmounts: [50, 100, 200, 500],
    allowMonthly: true,
    allowOneTime: true,
  },
  offering: {
    key: "offering",
    label: "Offering",
    description: "A voluntary gift of worship and gratitude.",
    suggestedAmounts: [50, 100, 200, 500],
    allowMonthly: true,
    allowOneTime: true,
  },
  general: {
    key: "general",
    label: "General Donation",
    description: "A flexible gift used where needed most.",
    suggestedAmounts: [50, 100, 200, 500],
    allowMonthly: true,
    allowOneTime: true,
  },
};

function cleanText(value, fallback = "") {
  const text = String(value || "").trim();
  return text || fallback;
}

function cleanManualText(value, max = 700) {
  return String(value || "").replace(/\r\n/g, "\n").trim().slice(0, max);
}

const MANUAL_METHODS = [
  { key: "zelle", label: "Zelle" },
  { key: "cashApp", label: "Cash App" },
  { key: "paypal", label: "PayPal" },
  { key: "bankTransfer", label: "Bank transfer" },
  { key: "mobileMoney", label: "Mobile money" },
  { key: "check", label: "Check" },
  { key: "cash", label: "Cash" },
];

function hasActionableManualMethodDetails(key, details = {}) {
  if (!details?.enabled) return false;
  if (key === "zelle") {
    return Boolean(details.emailOrPhone);
  }
  if (key === "cashApp") {
    return Boolean(details.tag);
  }
  if (key === "paypal") {
    return Boolean(details.url || details.email || details.instructions);
  }
  if (key === "bankTransfer") {
    return Boolean(
      details.bankName ||
        details.accountName ||
        details.accountNumberDisplay ||
        details.accountNumberLast4 ||
        details.routingNumber ||
        details.ibanMasked ||
        details.swiftMasked ||
        details.instructions
    );
  }
  if (key === "mobileMoney") {
    return Boolean(details.provider || details.phone || details.accountName || details.instructions);
  }
  if (key === "check") {
    return Boolean(details.payableTo || details.mailingAddress || details.instructions);
  }
  if (key === "cash") {
    return Boolean(details.instructions);
  }
  return false;
}

export function normalizePublicGivingCategory(category, fallback = "general") {
  const normalized = normalizeGivingPlatformCategory(category, fallback);
  if (normalized === "tithes") return "tithe";
  if (normalized === "building" || normalized === "building_fund") return "building_fund";
  return normalized || fallback;
}

function normalizeAmountList(values, fallback = DEFAULT_AMOUNTS) {
  const amounts = (Array.isArray(values) ? values : [])
    .map((value) => Number(String(value || "").replace(/[^\d.]/g, "")))
    .filter((value) => Number.isFinite(value) && value > 0)
    .map((value) => Math.round(value * 100) / 100);
  return amounts.length ? Array.from(new Set(amounts)).slice(0, 6) : fallback;
}

function normalizeCategoryRow(row = {}, fallbackKey = "general") {
  const key = normalizePublicGivingCategory(row.key || row.id || row.category || fallbackKey, fallbackKey);
  const fallback = DEFAULT_CATEGORY_CONFIG[key] || {
    key,
    label: key
      .split("_")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ") || "General",
    description: "Support this giving area through the organization.",
    suggestedAmounts: DEFAULT_AMOUNTS,
    allowMonthly: true,
    allowOneTime: true,
  };
  const suggestedAmounts = normalizeAmountList(
    row.suggestedAmounts || row.amounts || row.presets,
    fallback.suggestedAmounts
  );
  return {
    ...fallback,
    ...row,
    key,
    enabled: row.enabled !== false,
    label: cleanText(row.label || row.title || row.name, fallback.label),
    description: cleanText(row.description || row.body || row.helper, fallback.description),
    suggestedAmounts,
    defaultAmount: Number(row.defaultAmount) > 0 ? Number(row.defaultAmount) : suggestedAmounts[0],
    allowMonthly: row.allowMonthly !== false && row.recurringEnabled !== false,
    allowOneTime: row.allowOneTime !== false,
  };
}

export function resolveGivingConfig(publicProfile = {}, org = {}) {
  const raw =
    publicProfile?.givingConfig ||
    org?.publicProfile?.givingConfig ||
    org?.givingConfig ||
    {};
  const configuredRows = Array.isArray(raw.categories) ? raw.categories : [];
  const fallbackRows = ["campaign", "tithe", "offering", "general"].map((key) => DEFAULT_CATEGORY_CONFIG[key]);
  const rows = (configuredRows.length ? configuredRows : fallbackRows)
    .map((row, index) => normalizeCategoryRow(row, row?.key || fallbackRows[index]?.key || "general"))
    .filter((row) => row.enabled !== false);
  const categories = rows.length ? rows : fallbackRows.map((row) => normalizeCategoryRow(row, row.key));
  const defaultCategory = normalizePublicGivingCategory(raw.defaultCategory, categories[0]?.key || "general");
  return {
    enabled: raw.enabled !== false,
    defaultCategory: categories.some((row) => row.key === defaultCategory) ? defaultCategory : categories[0]?.key || "general",
    categories,
    receiptNote: cleanText(raw.receiptNote || org?.receiptTaxNote, ""),
    taxDeductible: raw.taxDeductible === true,
  };
}

export function getGivingCategoryConfig(givingConfig, category) {
  const normalized = normalizePublicGivingCategory(category, givingConfig?.defaultCategory || "general");
  return (
    (givingConfig?.categories || []).find((row) => row.key === normalized) ||
    normalizeCategoryRow(DEFAULT_CATEGORY_CONFIG[normalized] || {}, normalized)
  );
}

export function getEnabledManualPaymentMethods(payments = {}) {
  const manual = normalizePublicManualPaymentInstructions(payments);
  return MANUAL_METHODS
    .map((method) => ({ ...method, details: manual[method.key] }))
    .filter((method) => hasActionableManualMethodDetails(method.key, method.details));
}

export function normalizePublicManualPaymentInstructions(payments = {}) {
  const manual = payments?.manual || {};
  return {
    memoInstructions: cleanManualText(manual.memoInstructions, 500),
    publicNote: cleanManualText(manual.publicNote, 500),
    zelle: {
      enabled: manual.zelle?.enabled === true,
      displayName: cleanText(manual.zelle?.displayName || manual.zelle?.zelleName, ""),
      emailOrPhone: cleanText(manual.zelle?.emailOrPhone || manual.zelle?.email || manual.zelle?.phone, ""),
      qrCodeUrl: cleanText(manual.zelle?.qrCodeUrl || manual.zelle?.qrImageUrl || manual.zelle?.qrCode, ""),
      instructions: cleanManualText(manual.zelle?.instructions),
    },
    cashApp: {
      enabled: manual.cashApp?.enabled === true,
      displayName: cleanText(manual.cashApp?.displayName || manual.cashApp?.cashAppName, ""),
      tag: cleanText(manual.cashApp?.tag, "").replace(/^\$/, ""),
      qrCodeUrl: cleanText(manual.cashApp?.qrCodeUrl || manual.cashApp?.qrImageUrl || manual.cashApp?.qrCode, ""),
      instructions: cleanManualText(manual.cashApp?.instructions),
    },
    paypal: {
      enabled: manual.paypal?.enabled === true,
      url: cleanText(manual.paypal?.url, ""),
      email: cleanText(manual.paypal?.email, ""),
      instructions: cleanManualText(manual.paypal?.instructions),
    },
    bankTransfer: {
      enabled: manual.bankTransfer?.enabled === true,
      bankName: cleanText(manual.bankTransfer?.bankName, ""),
      accountName: cleanText(manual.bankTransfer?.accountName, ""),
      accountNumberDisplay: cleanText(
        manual.bankTransfer?.accountNumberDisplay || manual.bankTransfer?.accountNumberMasked,
        ""
      ),
      accountNumberLast4: cleanText(manual.bankTransfer?.accountNumberLast4, ""),
      routingNumber: cleanText(manual.bankTransfer?.routingNumber || manual.bankTransfer?.routingNumberMasked, ""),
      ibanMasked: cleanText(manual.bankTransfer?.ibanMasked, ""),
      swiftMasked: cleanText(manual.bankTransfer?.swiftMasked, ""),
      instructions: cleanManualText(manual.bankTransfer?.instructions),
    },
    mobileMoney: {
      enabled: manual.mobileMoney?.enabled === true,
      provider: cleanText(manual.mobileMoney?.provider, ""),
      country: cleanText(manual.mobileMoney?.country, ""),
      phone: cleanText(manual.mobileMoney?.phone, ""),
      accountName: cleanText(manual.mobileMoney?.accountName, ""),
      instructions: cleanManualText(manual.mobileMoney?.instructions),
    },
    check: {
      enabled: manual.check?.enabled === true,
      payableTo: cleanText(manual.check?.payableTo || manual.check?.payeeName, ""),
      mailingAddress: cleanManualText(manual.check?.mailingAddress, 500),
      instructions: cleanManualText(manual.check?.instructions),
    },
    cash: {
      enabled: manual.cash?.enabled === true,
      instructions: cleanManualText(manual.cash?.instructions),
    },
  };
}

export function manualPaymentMethodsAreConfigured(payments = {}) {
  const manual = normalizePublicManualPaymentInstructions(payments);
  return MANUAL_METHODS.some((method) =>
    hasActionableManualMethodDetails(method.key, manual[method.key])
  );
}

function mergeCampaignManualInstructions(orgPayments = {}, campaignPaymentInstructions = {}) {
  const orgManual = normalizePublicManualPaymentInstructions(orgPayments);
  const campaignManual = normalizePublicManualPaymentInstructions({
    manual: {
      ...(campaignPaymentInstructions?.manual || {}),
      memoInstructions: campaignPaymentInstructions?.memoInstructions || "",
      publicNote: campaignPaymentInstructions?.publicNote || "",
    },
  });
  return MANUAL_METHODS.reduce(
    (merged, method) => ({
      ...merged,
      [method.key]: hasActionableManualMethodDetails(method.key, campaignManual[method.key])
        ? campaignManual[method.key]
        : orgManual[method.key],
    }),
    {
      memoInstructions: campaignManual.memoInstructions || orgManual.memoInstructions,
      publicNote: campaignManual.publicNote || orgManual.publicNote,
    }
  );
}

export function resolveManualPaymentInstructionsForContext({
  orgPayments = {},
  campaignPaymentInstructions = null,
} = {}) {
  const mode = String(campaignPaymentInstructions?.mode || "default").toLowerCase();
  if (mode === "hidden") {
    return {
      ...(orgPayments || {}),
      manual: {},
      manualPaymentInstructionsMode: "hidden",
    };
  }
  if (mode === "custom") {
    return {
      ...(orgPayments || {}),
      manual: mergeCampaignManualInstructions(orgPayments, campaignPaymentInstructions),
      manualPaymentInstructionsMode: "campaign_custom",
    };
  }
  return {
    ...(orgPayments || {}),
    manual: normalizePublicManualPaymentInstructions(orgPayments || {}),
    manualPaymentInstructionsMode: "organization_default",
  };
}

export function hasStripePaymentMethod(payments = {}) {
  const stripe = payments?.stripe || {};
  return (
    stripe.enabled !== false &&
    Boolean(
      stripe.connectedAccountId ||
        payments.stripeConnectedAccountId ||
        stripe.chargesEnabled ||
        payments.chargesEnabled ||
        stripe.onboardingComplete ||
        payments.onboardingComplete
    )
  );
}
